import {getIsMobile} from "./isMobile";

const DEFAULT_MAP_OPTIONS = {
  planetOverviewZoom: 2,
  pointDetailZoom: 15,
  center: { lat: 44.83, lng: -30.44 },
  customMapStyles: [
    { featureType: 'all', elementType: 'all', stylers: [{ saturation: -100 }] },
    { featureType: 'landscape', elementType: 'geometry', stylers: [{ color: '#e6e6e6' }] },
    { featureType: 'poi', stylers: [{ visibility: 'off'}] },
    // Чтобы текст и дороги были читаемы на ЧБ карте
    { elementType: 'labels.text.fill', stylers: [{ color: '#222222' }] },
    { elementType: 'labels.text.stroke', stylers: [{ color: '#ffffff' }] },
    { featureType: 'road', elementType: 'geometry', stylers: [{ lightness: 60 }, { visibility: 'simplified' }] }
  ],
  controls: {
    zoomControl: true,
    mapTypeControl: false,
    scaleControl: true,
    streetViewControl: false,
    rotateControl: true,
    scrollwheel: true,
    gestureHandling: 'greedy',
    fullscreenControl: false
  },
  activePointId: null,
  smallIconZoomThreshold: 5,
  allCountriesZoomThreshold: 7,
  smallIconSize: 16,
  inactiveIconSize: 40,
  activeIconWidth: 58,
  activeIconHeight: 79,
  goldIconScale: 1.3,
  addressFocusZoom: 12,
  addressMaxZoom: 13,
}

export class GoogleMapWrapper {
  constructor(mapElement, initialPoints = [], options = {}) {
    if (!mapElement) {
      throw new Error("DOM элемент для карты не предоставлен.");
    }
    if (!google || !google.maps) {
      throw new Error("Google Maps API не загружен.");
    }
    
    this.options = {
      ...DEFAULT_MAP_OPTIONS,
      ...options,
    };
    
    this.mapElement = mapElement;
    this.map = null;
    this.markers = [];
    this.pointDataMap = new Map();
    this.activePointId = this.options.activePointId;
    
    this.zoomChangeListener = null;

    this.firstChange = false;

    const isMobile = getIsMobile()
    this.options.smallIconZoomThreshold = isMobile ? 6 : 5;

    this._initMap();
    this.displayNewPoints(initialPoints);
  }

  _callbacks = {
    onSetAllCountries: () => {}
  };

  setCallback(name, fn) {
    if (!name || !fn || typeof fn !== 'function') {
      throw new Error()
    }
    this._callbacks[name] = fn
  }
  
  _initMap() {
    this.map = new google.maps.Map(this.mapElement, {
      center: this.options.center,
      zoom: this.options.planetOverviewZoom,
      styles: this.options.customMapStyles,
      ...this.options.controls,
    });
    this.map.setOptions({minZoom: this.options.planetOverviewZoom});
    
    this.zoomChangeListener = this.map.addListener('zoom_changed', () => this._updateMarkersForZoom());
    this.zoomChangeListener = this.map.addListener('zoom_changed', () => this._updateCountryForZoom());
  }
  
  _clearMarkers() {
    this.markers.forEach(marker => marker.setMap(null));
    this.markers = [];
    this.pointDataMap.clear();
  }
  
  _addMarker(pointData) {
    if (!pointData || !pointData.coordinates || !pointData.icon) {
      console.warn("Некорректные данные для точки:", pointData);
      return;
    }
    
    const isActive = pointData.id === this.activePointId;
    const isGold = GoogleMapWrapper.isGoldPoint(pointData);
    
    const currentZoom = this.map.getZoom();
    const useSmallIcons = !isActive && currentZoom < this.options.smallIconZoomThreshold;
    
    let markerIcon;
    let iconState;
    
    if (isActive) {
      markerIcon = this._createMarkerIcon(pointData.icon.active, true, isGold);
      iconState = 'active';
    } else if (useSmallIcons) {
      markerIcon = this._createSmallMarkerIcon(pointData.icon.small, isGold);
      iconState = 'small';
    } else {
      markerIcon = this._createMarkerIcon(pointData.icon.inactive, false, isGold);
      iconState = 'normal';
    }
    
    const marker = new google.maps.Marker({
      position: pointData.coordinates,
      map: this.map,
      title: pointData.name,
      icon: markerIcon,
    });
    
    this.pointDataMap.set(pointData.id, { ...pointData, googleMarker: marker, iconState, isGold });
    this.markers.push(marker);
    
    marker.addListener('click', () => {
      this._handleMarkerClick(pointData.id);
    });
  }
  
  static isGoldPoint(pointData) {
    return String(pointData?.raw?.rating ?? '').toLowerCase() === 'gold';
  }

  _createMarkerIcon(imgUrl, isActive, isGold = false) {
    const scale = isGold ? this.options.goldIconScale : 1;
    const width = (isActive ? this.options.activeIconWidth : this.options.inactiveIconSize) * scale;
    const height = (isActive ? this.options.activeIconHeight : this.options.inactiveIconSize) * scale;

    return {
      url: imgUrl,
      scaledSize: new google.maps.Size(width, height),
      origin: new google.maps.Point(0, 0),
      anchor: new google.maps.Point(width / 2, isActive ? height : height / 2)
    }
  }
  _createSmallMarkerIcon(imgUrl, isGold = false) {
    const size = this.options.smallIconSize * (isGold ? this.options.goldIconScale : 1);
    return {
      url: imgUrl,
      scaledSize: new google.maps.Size(size, size),
      origin: new google.maps.Point(0, 0),
      anchor: new google.maps.Point(size / 2, size / 2)
    };
  }

  /**
   * Следит за изменение zoom
   * При срабатывании условия изменяет иконку каждого маркера
   */
  _updateMarkersForZoom() {
    const currentZoom = this.map.getZoom();
    const useSmallIcons = currentZoom < this.options.smallIconZoomThreshold;
    
    this.pointDataMap.forEach((pointInfo, pointId) => {
      // Активный маркер не трогаем
      if (pointId === this.activePointId) {
        return;
      }
      
      const { googleMarker, icon, iconState, isGold } = pointInfo;
      
      if (useSmallIcons) {
        // Если иконки должны быть маленькими, а они еще нет - меняем
        if (iconState !== 'small') {
          googleMarker.setIcon(this._createSmallMarkerIcon(icon.small, isGold));
          pointInfo.iconState = 'small';
        }
      } else {
        // Если иконки должны быть обычными, а они еще нет - меняем
        if (iconState !== 'normal') {
          googleMarker.setIcon(this._createMarkerIcon(icon.inactive, false, isGold));
          pointInfo.iconState = 'normal';
        }
      }
    });
  }

  /**
   * Следит за изменение zoom
   * При срабатывании условия вызывает callback на выбор фильтра (All countries)
   */
  _updateCountryForZoom() {
    const currentZoom = this.map.getZoom();

    if (currentZoom < this.options.allCountriesZoomThreshold && this.firstChange) {
      if (typeof this._callbacks.onSetAllCountries === 'function') {
        this._callbacks.onSetAllCountries()
      }
    };

    this.firstChange = true;
  }
  
  _handleMarkerClick(pointId) {
    const pointInfo = this.pointDataMap.get(pointId);
    if (!pointInfo || typeof pointInfo.callback !== 'function') {
      return;
    }
    pointInfo.callback(pointId);
  }
  
  /**
   * Устанавливает масштаб карты так, чтобы все текущие маркеры были видны.
   * Если маркеров нет, устанавливает "обзор планеты".
   */
  _fitBoundsToMarkers() {
    if (this.markers.length === 0) {
      this.map.setCenter({ lat: 0, lng: 0 });
      this.map.setZoom(this.options.planetOverviewZoom);
    } else if (this.markers.length === 1) {
      this.map.setCenter({ lat: this.markers[0].position.lat(), lng: this.markers[0].position.lng() });
      this.map.setZoom(this.options.pointDetailZoom - 2);
    } else {
      const bounds = new google.maps.LatLngBounds();
      this.markers.forEach(marker => bounds.extend(marker.getPosition()));
      this.map.fitBounds(bounds)
    }
  }
  
  /**
   * Отображает новые точки на карте.
   * Старые точки удаляются. Масштаб устанавливается для отображения всех новых точек.
   */
  displayNewPoints(newPoints = [], updateZoom = true) {
    this._clearMarkers();
    
    newPoints.forEach(point => this._addMarker(point));
    
    if (updateZoom) {
      this._fitBoundsToMarkers();
    }
  }
  
  /**
   * Устанавливает точке активность по ее ID.
   * Устанавливает масштабирование
   */
  setActivePointById(pointId, needPanAndZoom = false) {
    if (this.activePointId && this.activePointId !== pointId) {
      this.removeActivePoint(false);
    }
    
    const pointInfo = this.pointDataMap.get(pointId);
    if (!pointInfo || !pointInfo.googleMarker) {
      console.warn(`Точка с ID '${pointId}' не найдена.`);
      return null;
    }
    
    // Активируем новый
    const { googleMarker, icon, coordinates, isGold } = pointInfo;
    googleMarker.setIcon(this._createMarkerIcon(icon.active, true, isGold));
    this.activePointId = pointId;
    pointInfo.iconState = 'active';
    
    if (needPanAndZoom) {
      this.map.panTo(coordinates);
      this.map.setZoom(this.options.pointDetailZoom);
    }
  }
  
  focusOnLocation(location, nearbyPoints = []) {
    if (!location || !this.map) {
      return;
    }

    if (!nearbyPoints.length) {
      this.map.panTo(location);
      this.map.setZoom(this.options.addressFocusZoom);
      return;
    }

    const bounds = new google.maps.LatLngBounds();
    bounds.extend(location);
    nearbyPoints.forEach(point => bounds.extend(point.coordinates));
    this.map.fitBounds(bounds);

    google.maps.event.addListenerOnce(this.map, 'idle', () => {
      if (this.map.getZoom() > this.options.addressMaxZoom) {
        this.map.setZoom(this.options.addressMaxZoom);
      }
    });
  }

  removeActivePoint(needPanAndZoom = false) {
    if (this.activePointId) {
      const activePointInfo = this.pointDataMap.get(this.activePointId);
      if (activePointInfo) {
        const currentZoom = this.map.getZoom();
        const useSmallIcons = currentZoom < this.options.smallIconZoomThreshold;
        
        if (useSmallIcons) {
          activePointInfo.googleMarker.setIcon(this._createSmallMarkerIcon(activePointInfo.icon.small, activePointInfo.isGold));
          activePointInfo.iconState = 'small';
        } else {
          activePointInfo.googleMarker.setIcon(this._createMarkerIcon(activePointInfo.icon.inactive, false, activePointInfo.isGold));
          activePointInfo.iconState = 'normal';
        }
      }
      this.activePointId = null;
    }
    
    if (needPanAndZoom) {
      this._fitBoundsToMarkers();
    }
  }
  
  /**
   * Полное удаление экземпляра карты.
   * Очищает все ресурсы, удаляет карту из DOM (очищает контейнер).
   */
  destroy() {
    this._clearMarkers();
    
    if (this.map && this.zoomChangeListener) {
      google.maps.event.removeListener(this.zoomChangeListener);
    }
    
    if (this.map) {
      google.maps.event.clearInstanceListeners(this.map);
    }
    
    if (this.mapElement) {
      this.mapElement.innerHTML = '';
    }
    
    this.map = null;
    this.mapElement = null;
    this.pointDataMap = null;
    this.activePointId = null;
    this.markers = null;
    this.options = null;
    this.zoomChangeListener = null;
  }
}
